/**
 * Admin Workspace — route configuration.
 * Publicly reachable internal tool protected by a current staff profile.
 * Site access code is required before login and every other route except /health.
 */
import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AccessCodeGate } from './components/AccessCodeGate';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppLayout } from './components/AppLayout';
import { LoginPage } from './pages/LoginPage';
import { HealthPage } from './pages/HealthPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { DashboardPage } from './pages/DashboardPage';
import { CustomersPage } from './pages/CustomersPage';
import { CustomerDetailPage } from './pages/CustomerDetailPage';
import { LocationsPage } from './pages/LocationsPage';
import { LocationDetailPage } from './pages/LocationDetailPage';
import { LocationDocumentsPage } from './pages/LocationDocumentsPage';
import { LocationNetworkPage } from './pages/LocationNetworkPage';
import { LocationOperationsPage } from './pages/LocationOperationsPage';
import { LocationRacksPage } from './pages/LocationRacksPage';
import { TicketsPage } from './pages/TicketsPage';
import { TicketDetailPage } from './pages/TicketDetailPage';
import { AuditPage } from './pages/AuditPage';
import { TripsPage } from './pages/TripsPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { TripBillingPage } from './pages/TripBillingPage';
import { TripRunsPage } from './pages/TripRunsPage';
import { TripImportsPage } from './pages/TripImportsPage';
import { TripAtlasPage } from './pages/TripAtlasPage';
import { RolesPage } from './pages/RolesPage';
import { RevenuePage } from './pages/RevenuePage';
import { CommandCenterPage } from './pages/CommandCenterPage';
import { ZohoOAuthCallbackPage } from './pages/ZohoOAuthCallbackPage';
import { ProvisioningPage } from './pages/ProvisioningPage';
import { ZohoSyncPage } from './pages/ZohoSyncPage';
import { PortalAccessPage } from './pages/PortalAccessPage';
import { AuvikMappingPage } from './pages/AuvikMappingPage';
import { IntegrationStatusPage } from './pages/IntegrationStatusPage';
import { OnboardPilotPage } from './pages/OnboardPilotPage';
import { PropertyDirectoryPage } from './pages/PropertyDirectoryPage';
import { CrmEntryPage } from './pages/CrmEntryPage';
import { DeviceTypesPage } from './pages/DeviceTypesPage';
import { DevicesPage } from './pages/DevicesPage';
import { MissionControlMigrationPage } from './pages/MissionControlMigrationPage';

const NetworkOperations = lazy(() => import('./mission-control/NetworkOperations.jsx'));

export default function App() {
  return (
    <Routes>
      <Route path="/health" element={<HealthPage />} />

      <Route element={<AccessCodeGate />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/oauth/zoho/callback" element={<ZohoOAuthCallbackPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="crm/account/:zohoAccountId" element={<CrmEntryPage />} />
            <Route path="command-center" element={<CommandCenterPage />} />
            <Route path="network-ops/migration" element={<MissionControlMigrationPage />} />
            <Route path="network-ops/*" element={<Suspense fallback={<p role="status" className="text-sm text-text-muted">Loading network operations…</p>}><NetworkOperations /></Suspense>} />
            <Route path="customers" element={<CustomersPage />} />
            <Route path="customers/:id" element={<CustomerDetailPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="locations/:id" element={<LocationDetailPage />} />
          <Route path="locations/:id/tickets" element={<TicketsPage />} />
          <Route path="locations/:id/tickets/:ticketId" element={<TicketDetailPage />} />
          <Route path="locations/:id/documents" element={<LocationDocumentsPage />} />
          <Route path="locations/:id/network" element={<LocationNetworkPage />} />
          <Route path="locations/:id/operations" element={<LocationOperationsPage />} />
          <Route path="locations/:id/racks" element={<LocationRacksPage />} />
            <Route path="properties" element={<PropertyDirectoryPage />} />
            <Route path="devices" element={<DevicesPage />} />
            <Route path="device-types" element={<DeviceTypesPage />} />
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
            <Route path="revenue" element={<RevenuePage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="trips" element={<TripsPage />} />
            <Route path="runs" element={<TripRunsPage />} />
            <Route path="trips/imports" element={<TripImportsPage />} />
            <Route path="trips/atlas" element={<TripAtlasPage />} />
            <Route path="trips/atlas/:locationId" element={<TripAtlasPage />} />
            <Route path="invoices" element={<InvoicesPage />} />
            <Route path="billing" element={<TripBillingPage />} />
            <Route path="roles" element={<RolesPage />} />
            <Route path="provisioning" element={<ProvisioningPage />} />
            <Route path="onboard" element={<OnboardPilotPage />} />
            <Route path="zoho-sync" element={<ZohoSyncPage />} />
            <Route path="portal-access" element={<PortalAccessPage />} />
            <Route path="integrations" element={<IntegrationStatusPage />} />
            <Route path="integrations/auvik" element={<AuvikMappingPage />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
